import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Button, Modal } from 'ncoded-component-library';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import DataRoomMembersAccessField from 'components/DataRoomMembersAccessField';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';

import './RfiGrantFileAccessModal.styles.scss';

type RfiGrantFileAccessModalProps = {
  className?: string;
  dataRoomRfiId: string;
  grantAccessDataRoomMemberIds: string[];
  onRfiUpdate: (rfi: DataRoomRFI) => void;
};

const RfiGrantFileAccessModal: ForwardRefRenderFunction<
  ModalRef,
  RfiGrantFileAccessModalProps
> = (
  { className, dataRoomRfiId, grantAccessDataRoomMemberIds, onRfiUpdate },
  ref,
) => {
  const classes = classNames('rfi-grant-file-access-modal', className);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();
  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const onSubmit = useCallback(
    async (values) => {
      try {
        const { data } = await dataRoomApi.rfi.patchRfi(dataRoomRfiId, values);

        popSuccess(t('succesfullyUpdatedRFI'));
        onRfiUpdate(data);
        modal?.close();
      } catch (e) {
        popServerError(e);
      }
    },
    [dataRoomRfiId, modal, onRfiUpdate, popServerError, popSuccess, t],
  );

  return (
    <Modal ref={modalRef} className={classes} title={t('grantFileAccess')}>
      <Form
        initialValues={{ grantAccessDataRoomMemberIds }}
        onSubmit={onSubmit}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit}>
            <p className="rfi-grant-file-access-modal__description">
              {t('grantAccessStepDescription')}
            </p>

            <DataRoomMembersAccessField name="grantAccessDataRoomMemberIds" />

            <div className="rfi-grant-file-access-modal__actions">
              <Button type="submit" disabled={submitting}>
                {t('done')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(RfiGrantFileAccessModal);
